import { memo, useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import moment from 'moment-timezone';
import _isEmpty from 'lodash/isEmpty';
import _map from 'lodash/map';
import _range from 'lodash/range';
import _trim from 'lodash/trim';
//Local files
import { CardItem } from './PenStudyFlows.styled';
import PenStudyAnalysesList from 'components/Analyses/PenStudyAnalyses/List/List';
import ErrorWrapper from 'components/Common/ErrorWrapper/ErrorWrapper';
import useApp from 'hooks/useApp';
import useCustomSelector from 'hooks/useCustomSelector';
import useSuccess from 'hooks/useSuccess';
import usePenStudyFlows from 'hooks/usePenStudyFlows';
import usePenStudyAnalyses from 'hooks/usePenStudyAnalyses';
import { DELETE_PEN_STUDY_FLOW } from 'helpers/confirmations';
import { checkEmptyString, needToUpdateField } from 'helpers';
import { timezones } from 'helpers/constants';
import { penStudyFlowsSelectors } from "features/penStudyFlows";

const MIN_DAYS = 1;
const MAX_DAYS = 7;

const PenStudyFlows = () => {
  const { openDeleteDialog } = useApp();
  const { setSuccess } = useSuccess();
  const { getPenStudyFlow, createPenStudyFlow, updatePenStudyFlow } = usePenStudyFlows();
  const { getPenStudyAnalyses, clearLocalPenStudyAnalyses } = usePenStudyAnalyses();
  const id = useCustomSelector(penStudyFlowsSelectors.getPenStudyFlowId);
  const penStudyFlow = useCustomSelector(penStudyFlowsSelectors.getPenStudyFlowData);
  const fetching = useCustomSelector(penStudyFlowsSelectors.penStudyFlowIsFetching);
  const [beforeIn, setBeforeIn] = useState('P1D');
  const [startAt, setStartAt] = useState({ value: '', formattedValue: null, error: '' });
  const [timezone, setTimezone] = useState('US/Eastern');
  const [prompt, setPrompt] = useState({ value: '', error: '' });
  const [upload, setUpload] = useState(false);
  const [edit, setEdit] = useState(false);
  const [firstTimeOpened, setFirstTimeOpened] = useState(true);
  const beforeInArray = _map(_range(MIN_DAYS, MAX_DAYS + 1), day => `P${day}D`);

  const handleEntering = (pen_study_flow) => {
    const todayDate = moment().startOf('day');
    const startAtFromServer = moment.utc(pen_study_flow?.start_at);
    const startAtWithTodayDate = startAtFromServer
      .set({
        year: todayDate.year(),
        month: todayDate.month(),
        date: todayDate.date(),
      })
      .tz(pen_study_flow?.time_zone);
    const startAtFinal = startAtWithTodayDate.format('YYYY-MM-DDTHH:mm:ss');
    
    setBeforeIn(pen_study_flow?.before_in);
    setStartAt({ value: startAtFinal, formattedValue: new Date(startAtFinal), error: '' });
    setTimezone(pen_study_flow?.time_zone);
    setPrompt({ value: pen_study_flow?.prompt, error: '' });
    setUpload(pen_study_flow?.upload);
  };
  const handleExited = () => {
    setBeforeIn('P1D');
    setStartAt({ value: '', formattedValue: null, error: '' });
    setTimezone('US/Eastern');
    setPrompt({ value: '', error: '' });
    setUpload(false);
    setEdit(false);
  };
  const handleSave = () => {
    let hasError = false;

    if (!checkEmptyString(startAt.value)) {
      setStartAt(prev => ({ ...prev, error: `Start At can't be empty` }) );
      hasError = true;
    }
    if (!checkEmptyString(prompt.value)) {
      setPrompt(prev => ({ ...prev, error: `Prompt can't be empty` }) );
      hasError = true;
    }
    if (hasError) return;

    const startAtUTC = moment.tz(startAt.value, timezone).utc().format();

    const pen_study_flow = {
      ...(needToUpdateField(penStudyFlow?.before_in, beforeIn) && { before_in: beforeIn }),
      ...(needToUpdateField(penStudyFlow?.start_at, startAtUTC) && { start_at: startAtUTC }),
      ...(needToUpdateField(penStudyFlow?.time_zone, timezone) && { time_zone: timezone }),
      ...(needToUpdateField(penStudyFlow?.prompt, prompt.value) && { prompt: prompt.value }),
      ...(needToUpdateField(penStudyFlow?.upload, upload) && { upload }),
    };

    if (!_isEmpty(pen_study_flow)) {
      if (!!id) {
        updatePenStudyFlow(pen_study_flow)
        .then(({ error }) => {
          if (!error) {
            setSuccess('Flow successfully updated');
          }
        })
        .finally(() => setEdit(false))
      } else {
        createPenStudyFlow(pen_study_flow)
        .then(({ error }) => {
          if (!error) {
            setSuccess('Flow successfully created');
          }
        })
      }
    } 
  }
  const handleUpload = () => setUpload(!upload);
  const handleDelete = () => openDeleteDialog({ id: '', type: DELETE_PEN_STUDY_FLOW });
  const fetchPenStudyAnalyses = useCallback(() => {
    getPenStudyAnalyses({ offset: 0 });
  }, [getPenStudyAnalyses]);
  
  useEffect(() => {
    if (!id) {
      handleExited();
      clearLocalPenStudyAnalyses();
    };
  }, [id, clearLocalPenStudyAnalyses]);
  useEffect(() => {
    if (firstTimeOpened) {
      getPenStudyFlow()
      .then(({ payload, error }) => {
        if (!error) {
          const { pen_study_flow } = payload;
          handleEntering(pen_study_flow);
          fetchPenStudyAnalyses();
        }
      })
      .finally(() => setFirstTimeOpened(false));
    }
  }, [getPenStudyFlow, fetchPenStudyAnalyses, firstTimeOpened]);

  return (
    <Box sx={{ flexGrow: 1, overflow: 'auto', p: 5 }}>
      <Container maxWidth={'sm'}>
        <Card sx={{ p: 2 }}>
          <CardItem>
            <Typography sx={{ fontSize: '1.75rem', fontWeight: 600 }}>Configuration</Typography>
            <Box>
              {!id && <Button variant='outlined' disabled={fetching} onClick={handleSave}>Create</Button>}
              {id && !edit && <Button variant='outlined' disabled={fetching} onClick={() => setEdit(true)}>Edit</Button>}
              {id && edit && <Button variant='outlined' disabled={fetching} sx={{ mr: '5px' }} onClick={handleSave}>Update</Button>}
              {id && edit && <Button variant='outlined' sx={{ color: '#E30000', borderColor: '#E30000' }} disabled={fetching} onClick={handleDelete}>Delete</Button>}
            </Box>
          </CardItem>
          <CardItem sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
            <TextField
              fullWidth
              margin='dense'
              select
              disabled={fetching || (!!id && !edit)}
              label='Days Before'
              value={beforeIn}
              onChange={({ target: { value } }) => setBeforeIn(value)}
            >
              {_map(beforeInArray, n => <MenuItem key={n} value={n}>{_trim(n, 'PD')}</MenuItem>)}
            </TextField>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <TimePicker
                label="Start at"
                value={startAt.formattedValue}
                disabled={fetching || (!!id && !edit)}
                onChange={(newValue) => {
                  const newDate = !!newValue && newValue?.toString() !== 'Invalid Date' ? newValue : '';
                  const formattedValue = !!newDate ? new Date(newDate) : null;
                  const value = !!newDate ? moment(newDate).format('YYYY-MM-DDTHH:mm:ss') : '';
                  setStartAt({
                    value,
                    formattedValue,
                    error: !newDate ? 'Date is invalid' : '',
                  });
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    fullWidth 
                    margin='dense' 
                    helperText={startAt.error} 
                    error={!!startAt.error} 
                  />
                )}
              />
            </LocalizationProvider>
            <TextField
              fullWidth
              margin='dense'
              select
              label='Timezone'
              value={timezone}
              onChange={({ target: { value } }) => setTimezone(value)}
              disabled={fetching || (!!id && !edit)}
            >
              {timezones.map((tz) => (
                <MenuItem key={tz} value={tz}>{tz}</MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              label='Prompt'
              multiline
              minRows={3}
              maxRows={10}
              disabled={fetching || (!!id && !edit)}
              helperText={prompt.error}
              error={!!prompt.error}
              value={prompt.value}
              onChange={({ target: { value } }) => setPrompt({ value, error: '' })}
            />
            <Box display='flex' justifyContent='center' width='100%'>
              <Box sx={{ fontWeight: 600 }}>Documents upload enabled</Box>
              <input type='checkbox' checked={upload} disabled={fetching || (!!id && !edit)}  onChange={handleUpload} />
            </Box>
          </CardItem>
        </Card>
        <Card sx={{ p: 2, mt: 3 }}>
          <Typography sx={{ fontSize: '1.75rem', fontWeight: 600 }}>History</Typography>
          <PenStudyAnalysesList />
        </Card>    
      </Container>
    </Box>
  );
};

const WrappedComponent = props => {
  return (
    <ErrorWrapper>
      <PenStudyFlows {...props} />
    </ErrorWrapper>
  );
};

export default memo(WrappedComponent);